import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import BackButton from '../../components/BackButton';
import api from '../../services/api';
import { 
  addMessageHandler, 
  sendDirectMessage, 
  isConnected, 
  initializeWebSocket, 
  subscribeToTopic,
  sendTypingIndicator,
  sendDeliveryConfirmation,
  sendReadReceipt,
  subscribeToTypingIndicator,
  subscribeToDeliveryStatus,
  subscribeToReadReceipts
} from '../../services/websocketManager';
import './DirectChat.css';

const DirectChat = () => {
  const { username } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [otherUser, setOtherUser] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => {
    // Reset messages when username changes (navigating to different chat)
    setMessages([]);
    setLoading(true);
    
    // Load initial messages
    loadMessages();
    loadOtherUserProfile();

    // Add message handler for this component FIRST
    const handleMessage = (message) => {
      console.log('=== DirectChat: Received WebSocket message ===');
      console.log('Full message object:', message);
      console.log('Current user:', user?.username);
      console.log('Chatting with:', username);
      
      // Handle incoming message - check both field names for compatibility
      const sender = message.senderUsername || message.sender;
      const recipient = message.recipientUsername || message.recipient;
      
      console.log('Extracted sender:', sender);
      console.log('Extracted recipient:', recipient);
      
      // Only add message if it's for this conversation
      const currentUsername = user?.username;
      const isForThisChat = (sender === username && recipient === currentUsername) || 
                           (sender === currentUsername && recipient === username);
      
      console.log('Is for this chat?', isForThisChat);
      console.log('Comparison:', {
        'sender === username': sender === username,
        'recipient === currentUsername': recipient === currentUsername,
        'sender === currentUsername': sender === currentUsername,
        'recipient === username': recipient === username
      });
      
      if (isForThisChat) {
        console.log('✅ Adding message to conversation:', message);
        
        // Send delivery confirmation if this is a received message
        const currentUsername = user?.username;
        if (sender === username && recipient === currentUsername && message.id) {
          sendDeliveryConfirmation(message.id, currentUsername);
        }
        
        setMessages(prev => {
          // Avoid duplicates
          const exists = prev.some(m => 
            m.id === message.id || 
            (m.senderUsername === sender && 
             m.recipientUsername === recipient && 
             m.content === message.content &&
             Math.abs(new Date(m.timestamp) - new Date(message.timestamp || Date.now())) < 1000)
          );
          if (exists) {
            console.log('⚠️ Message already exists, skipping');
            return prev;
          }
          console.log('✅ Message added to state');
          return [...prev, {
            id: message.id || Date.now(),
            senderUsername: sender,
            recipientUsername: recipient,
            content: message.content,
            timestamp: message.timestamp || new Date().toISOString(),
            isRead: message.isRead || false,
            isDelivered: message.isDelivered || false
          }];
        });
      } else {
        console.log('❌ Message not for this conversation:', { 
          sender, 
          recipient, 
          currentUser: user?.username, 
          chatWith: username,
          isForThisChat: false
        });
      }
    };

    const removeHandler = addMessageHandler(handleMessage);

    // Initialize WebSocket connection AFTER adding handler (global, shared)
    initializeWebSocket();

    // Subscribe to topic pattern for this specific conversation
    // Backend broadcasts to both /topic/direct/{sender}/{recipient} and /topic/direct/{recipient}/{sender}
    // We only need to subscribe to ONE pattern since both will receive the same message
    // We'll use the pattern where current user comes first alphabetically to ensure consistency
    let topicSubscriptions = [];
    const currentUsername = user?.username;
    
    const setupTopicSubscriptions = () => {
      if (currentUsername && username && isConnected()) {
        // Use a consistent pattern: always use the pattern where usernames are sorted alphabetically
        // This ensures both users subscribe to the same topic and prevents duplicates
        const [user1, user2] = [currentUsername, username].sort();
        const topic = `/topic/direct/${user1}/${user2}`;
        
        console.log('Subscribing to topic pattern:', topic);
        
        const subscription = subscribeToTopic(topic, handleMessage);
        if (subscription) topicSubscriptions.push(subscription);
      }
    };

    // Setup subscriptions after a short delay to ensure WebSocket is connected
    const subscriptionTimeout = setTimeout(() => {
      setupTopicSubscriptions();
    }, 1000);

    // Check connection status
    const checkConnection = setInterval(() => {
      setWsConnected(isConnected());
      // Try to setup subscriptions if not already done
      if (isConnected() && topicSubscriptions.length === 0 && currentUsername && username) {
        setupTopicSubscriptions();
      }
    }, 1000);

    // Subscribe to typing indicators
    const typingSubscription = subscribeToTypingIndicator(username, (event) => {
      if (event.sender === username && event.recipient === user?.username) {
        setOtherUserTyping(event.isTyping);
      }
    });

    // Subscribe to delivery status updates
    const deliverySubscription = subscribeToDeliveryStatus(username, (event) => {
      setMessages(prev => prev.map(msg => 
        msg.id === event.messageId ? { ...msg, isDelivered: true } : msg
      ));
    });

    // Subscribe to read receipts
    const readSubscription = subscribeToReadReceipts(username, (event) => {
      if (event.readBy === username && event.conversationWith === user?.username) {
        // Mark all messages from this user as read
        setMessages(prev => prev.map(msg => 
          msg.senderUsername === username ? { ...msg, isRead: true } : msg
        ));
      }
    });

    // Send read receipt when component mounts or messages change
    const sendReadReceiptTimeout = setTimeout(() => {
      if (messages.length > 0 && user?.username) {
        sendReadReceipt(username);
      }
    }, 1000);

    return () => {
      clearTimeout(subscriptionTimeout);
      clearTimeout(sendReadReceiptTimeout);
      clearInterval(checkConnection);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      removeHandler();
      if (typingSubscription) typingSubscription.unsubscribe();
      if (deliverySubscription) deliverySubscription.unsubscribe();
      if (readSubscription) readSubscription.unsubscribe();
      topicSubscriptions.forEach(sub => {
        try {
          if (sub && typeof sub.unsubscribe === 'function') {
            sub.unsubscribe();
          }
        } catch (e) {
          // Ignore
        }
      });
      topicSubscriptions = [];
      // Stop typing indicator when leaving
      if (isTyping) {
        sendTypingIndicator(username, false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, user?.username]); // Removed messages.length to prevent reloading on every message

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async () => {
    try {
      const response = await api.get(`/api/chat/messages/direct/${username}`);
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessages([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadOtherUserProfile = async () => {
    try {
      const response = await api.get(`/api/chat/profile/${username}`);
      setOtherUser(response.data);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const handleTyping = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    if (!isTyping) {
      setIsTyping(true);
      sendTypingIndicator(username, true);
    }
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Stop typing indicator after 3 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      sendTypingIndicator(username, false);
    }, 3000);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    // Stop typing indicator
    if (isTyping) {
      setIsTyping(false);
      sendTypingIndicator(username, false);
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    try {
      // Send via WebSocket if connected
      if (isConnected()) {
        console.log('Sending message via WebSocket to:', username);
        sendDirectMessage(username, newMessage);
        // Don't add optimistically - wait for WebSocket response to avoid duplicates
      } else {
        console.log('WebSocket not connected, using REST API');
        // Fallback to REST API
        await api.post('/api/chat/messages/direct', {
          recipientUsername: username,
          content: newMessage
        });
        loadMessages();
      }
      setNewMessage('');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to send message');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const blockUser = async () => {
    if (!window.confirm(`Are you sure you want to block ${username}?`)) return;
    
    try {
      await api.post(`/api/chat/block/${username}`);
      alert('User blocked successfully');
      navigate('/chat');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to block user');
    }
  };

  return (
    <div className="direct-chat">
      <BackButton />
      <div className="chat-header">
        <div className="chat-header-info">
          <h2>{otherUser?.displayName || username}</h2>
          {otherUser?.isPremium && <span className="badge-premium">⭐ Premium</span>}
          <span className={`ws-status ${wsConnected ? 'connected' : 'disconnected'}`}>
            {wsConnected ? '🟢 Online' : '🔴 Offline'}
          </span>
        </div>
        <button onClick={blockUser} className="btn-block">Block</button>
      </div>

      <div className="messages-container">
        {loading ? (
          <div className="loading">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="no-messages">No messages yet. Start the conversation!</div>
        ) : (
          messages.map(message => {
            const isOwnMessage = message.senderUsername === user?.username;
            return (
              <div
                key={message.id}
                className={`message ${isOwnMessage ? 'sent' : 'received'}`}
              >
                <div className="message-content">
                  <div className="message-sender">{message.senderUsername}</div>
                  <div className="message-text">{message.content}</div>
                  <div className="message-footer">
                    <div className="message-time">
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </div>
                    {isOwnMessage && (
                      <div className="message-status">
                        {message.isRead ? (
                          <span className="status-read" title="Read">✓✓</span>
                        ) : message.isDelivered ? (
                          <span className="status-delivered" title="Delivered">✓✓</span>
                        ) : (
                          <span className="status-sent" title="Sent">✓</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        {otherUserTyping && (
          <div className="typing-indicator">
            <span>{username} is typing...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={sendMessage} className="message-input-form">
              <input
                type="text"
                value={newMessage}
                onChange={handleTyping}
                placeholder="Type a message..."
                className="message-input"
              />
        <button type="submit" className="btn-send">Send</button>
      </form>
    </div>
  );
};

export default DirectChat;

